// iter570 — Extracted from BankStatementsPage.jsx (Reconciliation tab + its
// two dialogs: Investigate single row, and Batch AI Explain).
//
// Parent passes the loaded reconciliation payload + statement id; this
// component owns the local UI state (status filter, investigate dialog,
// batch-explain dialog) and the API handlers that drive those dialogs.
//
// The status filter is persisted to localStorage so the user's last choice
// survives reloads, matching the previous in-page behaviour.
import { cur } from '@/lib/currency';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, Sparkles, Eye, FileText, Landmark, Check, Bookmark, Link2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const statusMetaFor = (status) => ({
  matched:      { label: 'Matched ✓',  badge: 'bg-success/20 text-success',     row: '' },
  bank_higher:  { label: 'Bank > App',  badge: 'bg-orange-100 text-orange-700',  row: 'bg-orange-50/40' },
  app_higher:   { label: 'App > Bank',  badge: 'bg-rose-100 text-rose-700',      row: 'bg-rose-50/40' },
  bank_only:    { label: 'Bank Only',   badge: 'bg-warning/20 text-warning',     row: 'bg-warning/5' },
  app_only:     { label: 'App Only',    badge: 'bg-info/20 text-info',           row: 'bg-info/5' },
  mismatch:     { label: 'Mismatch',    badge: 'bg-error/20 text-error',         row: 'bg-error/5' },
}[status] || { label: status, badge: 'bg-muted', row: '' });

export function ReconciliationTab({
  statementId,
  statementPeriod,
  reconciliation,
  reconLoading,
  reconStatusFilter,
  setReconStatusFilter,
  fmt,
  fmt0,
  onNoteSaved,
}) {
  // Single-row drill-down + LLM explainer
  const [reconInvestigate, setReconInvestigate] = useState(null);
  // Whole-statement batch AI explain
  const [batchExplain, setBatchExplain] = useState(null);
  // Save-as-Note pending state
  const [savingNote, setSavingNote] = useState(false);

  const openReconInvestigate = async (row) => {
    setReconInvestigate({ deposit_date: row.deposit_date, branch: row.branch, status: row.status, loading: true, data: null, explainLoading: false, explanation: null });
    try {
      const r = await api.get(`/bank-statements/${statementId}/reconciliation/row-detail`, {
        params: { deposit_date: row.deposit_date, branch: row.branch },
      });
      setReconInvestigate(prev => ({ ...(prev || {}), loading: false, data: r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load row detail');
      setReconInvestigate(null);
    }
  };

  const autoExplainRecon = async () => {
    if (!reconInvestigate?.data) return;
    setReconInvestigate(prev => ({ ...(prev || {}), explainLoading: true, explanation: null }));
    try {
      const r = await api.post(
        `/bank-statements/${statementId}/reconciliation/explain`,
        null,
        { params: { deposit_date: reconInvestigate.deposit_date, branch: reconInvestigate.branch } }
      );
      setReconInvestigate(prev => ({ ...(prev || {}), explainLoading: false, explanation: r.data?.explanation || '' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate explanation');
      setReconInvestigate(prev => ({ ...(prev || {}), explainLoading: false }));
    }
  };

  const runBatchExplain = async () => {
    if (!statementId) return;
    setBatchExplain({ loading: true, narrative: null });
    try {
      const r = await api.post(`/bank-statements/${statementId}/reconciliation/explain-all`, null, {
        params: { max_rows: 25 },
      });
      setBatchExplain({ loading: false, ...r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch explain failed');
      setBatchExplain(null);
    }
  };

  const saveBatchExplainAsNote = async () => {
    if (!statementId || !batchExplain?.narrative) return;
    setSavingNote(true);
    try {
      await api.post(`/bank-statements/${statementId}/month-end-notes`, {
        title: `Monthly-close report · ${statementPeriod || ''}`.trim(),
        narrative: batchExplain.narrative,
        discrepancy_count: batchExplain.discrepancy_count,
        total_gap: batchExplain.total_gap,
        source: 'ai_batch_explain',
      });
      toast.success('Saved as Month-End Note · visible in Notes tab + audit trail');
      if (typeof onNoteSaved === 'function') onNoteSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  // iter577 — Auto-trigger the AI Monthly-Close Report when the URL has
  // `?ai=open`. Fires exactly once per statement load so a refresh doesn't
  // re-run the LLM call. The `ai` param is stripped immediately so it
  // doesn't trigger again on subsequent reconciliation reloads.
  const aiAutoTriggeredRef = useRef(null);
  useEffect(() => {
    if (!statementId || !reconciliation) return;
    if (aiAutoTriggeredRef.current === statementId) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('ai') !== 'open') return;
      aiAutoTriggeredRef.current = statementId;
      params.delete('ai');
      const newQuery = params.toString();
      const newUrl = `${window.location.pathname}${newQuery ? '?' + newQuery : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
      runBatchExplain();
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementId, reconciliation]);

  // iter577 — Build the audit-handoff URL (statement + recon filter + optional ai=open)
  const buildShareUrl = ({ includeAi = false } = {}) => {
    const u = new URL(window.location.href);
    if (statementId) u.searchParams.set('statement', statementId);
    if (reconStatusFilter && reconStatusFilter !== 'all') u.searchParams.set('recon', reconStatusFilter);
    else u.searchParams.delete('recon');
    if (includeAi) u.searchParams.set('ai', 'open');
    else u.searchParams.delete('ai');
    return u.toString();
  };

  const copyShareUrl = async (opts) => {
    const url = buildShareUrl(opts);
    const successMsg = opts?.includeAi
      ? 'AI report link copied — the auditor will land here with the AI Monthly-Close Report auto-opened.'
      : 'Auditor link copied — paste it in Slack/email so the auditor lands right here.';
    try {
      await navigator.clipboard.writeText(url);
      toast.success(successMsg);
    } catch {
      window.prompt('Copy this link manually:', url);
    }
  };

  return (
    <div className="space-y-4" data-testid="reconciliation-content-inner">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium">POS sales reconciliation</p>
          <p className="text-xs text-muted-foreground">Bank POS deposits vs Smarto sales — 1-day offset (today's sale → tomorrow's deposit)</p>
        </div>
        <div className="flex items-center gap-2">
          {reconciliation && reconciliation.rows && reconciliation.rows.some((r) => Math.abs(Number(r.difference) || 0) >= 1) && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl border-purple-300 text-purple-800 hover:bg-purple-50"
              onClick={() => runBatchExplain()}
              disabled={batchExplain?.loading}
              data-testid="recon-batch-explain-btn"
              title="Generate a monthly-close report explaining every discrepancy"
            >
              {batchExplain?.loading ? (
                <><Loader2 size={14} className="mr-1 animate-spin" /> Analysing…</>
              ) : (
                <><Sparkles size={14} className="mr-1" /> AI Explain All</>
              )}
            </Button>
          )}
          {reconciliation && (
            <Button size="sm" variant="outline" className="rounded-xl border-sky-300 text-sky-800 hover:bg-sky-50" onClick={() => copyShareUrl()} data-testid="recon-copy-link-btn" title="Copy a deep-link URL with the current filter pre-applied, for emailing the auditor">
              <Link2 size={14} className="mr-1" />Copy auditor link
            </Button>
          )}
          {/* iter618 — Why is this report in this currency? Links to the
              tenant tax-config page so multi-tenant onboarding can see
              where the country/currency choice comes from. */}
          <Button
            size="sm"
            variant="ghost"
            className="rounded-xl text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => { window.location.href = '/tax-settings'; }}
            data-testid="recon-currency-explainer-btn"
            title="Open the tenant tax-config page to see how country + currency are set"
          >
            <Sparkles size={11} className="mr-1" />Why is this in {reconciliation?.currency_symbol || 'SR'}?
          </Button>
          {reconciliation && (
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => {
              const csv = 'Deposit Date,Sale Date,Branch,Bank Amount,App Amount,Difference,Status\n' + reconciliation.rows.map(r => `${r.deposit_date},${r.sale_date},${r.branch},${r.bank_amount},${r.app_amount},${r.difference},${r.status}`).join('\n');
              const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'pos_reconciliation.csv'; a.click();
              toast.success('Exported');
            }} data-testid="export-reconciliation-btn"><Download size={14} className="mr-1" />Export CSV</Button>
          )}
        </div>
      </div>

      {/* Status-pills filter row */}
      {reconciliation && (
        <div className="flex flex-wrap gap-1.5" data-testid="recon-status-pills">
          {(() => {
            const s = reconciliation.summary;
            const discCount = (s.bank_higher_count || 0) + (s.app_higher_count || 0) + (s.bank_only_count || 0) + (s.app_only_count || 0);
            const pills = [
              { v: 'all',           label: 'All',         count: s.total_rows || 0,         color: 'bg-foreground text-background border-foreground' },
              { v: 'discrepancies', label: 'Discrepancies', count: discCount,                color: 'bg-warning/15 text-warning-foreground border-warning/40' },
              { v: 'matched',       label: 'Matched ✓',   count: s.matched_count || 0,      color: 'bg-success/15 text-success border-success/40' },
              { v: 'bank_higher',   label: 'Bank > App',  count: s.bank_higher_count || 0,  color: 'bg-orange-100 text-orange-800 border-orange-300' },
              { v: 'app_higher',    label: 'App > Bank',  count: s.app_higher_count || 0,   color: 'bg-rose-100 text-rose-800 border-rose-300' },
              { v: 'bank_only',     label: 'Bank Only',   count: s.bank_only_count || 0,    color: 'bg-amber-100 text-amber-800 border-amber-300' },
              { v: 'app_only',      label: 'App Only',    count: s.app_only_count || 0,     color: 'bg-sky-100 text-sky-800 border-sky-300' },
            ];
            return pills.map(p => {
              const isActive = reconStatusFilter === p.v;
              return (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setReconStatusFilter(p.v)}
                  disabled={p.count === 0 && p.v !== 'all'}
                  data-testid={`recon-pill-${p.v}`}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                    isActive
                      ? `${p.color} font-semibold ring-2 ring-foreground/20 shadow-sm`
                      : 'bg-background text-muted-foreground border-border hover:bg-muted/40'
                  } ${p.count === 0 && p.v !== 'all' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span>{p.label}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums ${
                    isActive ? 'bg-background/30' : 'bg-muted text-foreground'
                  }`}>
                    {p.count}
                  </span>
                </button>
              );
            });
          })()}
        </div>
      )}

      {reconLoading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="animate-spin mr-2" size={16} /> Loading reconciliation…</div>
      ) : !reconciliation ? (
        <div className="text-center py-10 text-muted-foreground text-sm">No reconciliation data available</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-3 bg-success/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Bank POS</div><div className="text-lg font-bold text-success">{fmt0(reconciliation.summary.total_bank_pos)}</div></div>
            <div className="p-3 bg-primary/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">App Sales</div><div className="text-lg font-bold text-primary">{fmt0(reconciliation.summary.total_app_sales)}</div></div>
            <div className={`p-3 rounded-xl text-center ${Math.abs(reconciliation.summary.total_difference) < 1 ? 'bg-success/10' : 'bg-error/10'}`}><div className="text-xs text-muted-foreground">Difference</div><div className={`text-lg font-bold ${Math.abs(reconciliation.summary.total_difference) < 1 ? 'text-success' : 'text-error'}`}>{fmt(reconciliation.summary.total_difference)}</div></div>
            <div className="p-3 bg-success/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Matched</div><div className="text-lg font-bold text-success">{reconciliation.summary.matched_count}</div></div>
            <div className="p-3 bg-warning/10 rounded-xl text-center"><div className="text-xs text-muted-foreground">Discrepancies</div><div className="text-lg font-bold text-warning">{reconciliation.summary.discrepancy_count}</div></div>
          </div>

          <div className="max-h-[500px] overflow-y-auto border rounded-xl">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="reconciliation-table">
                <thead className="sticky top-0 bg-muted/50 z-10">
                  <tr className="border-b">
                    <th className="text-left p-2 text-xs font-medium">Bank Date</th>
                    <th className="text-left p-2 text-xs font-medium">Sale Date</th>
                    <th className="text-left p-2 text-xs font-medium">Branch</th>
                    <th className="text-right p-2 text-xs font-medium">Bank POS</th>
                    <th className="text-right p-2 text-xs font-medium">App Sales</th>
                    <th className="text-right p-2 text-xs font-medium">Difference</th>
                    <th className="text-center p-2 text-xs font-medium">Status</th>
                    <th className="text-center p-2 text-xs font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const all = reconciliation.rows;
                    const filtered = reconStatusFilter === 'all' ? all
                      : reconStatusFilter === 'discrepancies' ? all.filter(r => r.status !== 'matched')
                      : all.filter(r => r.status === reconStatusFilter);
                    return filtered.map((r, i) => {
                      const statusMeta = statusMetaFor(r.status);
                      return (
                        <tr key={i} className={`border-b hover:bg-muted/50 ${statusMeta.row}`} data-testid={`recon-row-${i}`}>
                          <td className="p-2 text-sm">{r.deposit_date}</td>
                          <td className="p-2 text-sm">{r.sale_date || <span className="text-muted-foreground">-</span>}</td>
                          <td className="p-2 text-sm">{r.branch}</td>
                          <td className="p-2 text-sm text-right font-medium text-success">{r.bank_amount > 0 ? fmt(r.bank_amount) : '-'}</td>
                          <td className="p-2 text-sm text-right font-medium text-primary">{r.app_amount > 0 ? fmt(r.app_amount) : '-'}</td>
                          <td
                            className={`p-2 text-sm text-right font-bold ${
                              r.status === 'matched' ? 'text-success'
                              : r.status === 'bank_higher' ? 'text-orange-700'
                              : r.status === 'app_higher' ? 'text-rose-700'
                              : 'text-error'
                            }`}
                            title={r.tolerance ? `Tolerance ±${cur()} ${r.tolerance}` : undefined}
                          >
                            {r.difference !== 0 ? (r.difference > 0 ? '+' : '') + fmt(r.difference) : '-'}
                          </td>
                          <td className="p-2 text-center">
                            <Badge className={statusMeta.badge}>{statusMeta.label}</Badge>
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => openReconInvestigate(r)}
                              data-testid={`recon-investigate-btn-${i}`}
                              title="Drill into the bank txns + app sales behind this row"
                            >
                              <Eye size={12} className="mr-1" />Investigate
                            </Button>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                  {reconciliation.rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No POS transactions to reconcile. Ensure POS machines are mapped to branches.</td></tr>}
                  {reconciliation.rows.length > 0 && (() => {
                    const all = reconciliation.rows;
                    const filtered = reconStatusFilter === 'all' ? all
                      : reconStatusFilter === 'discrepancies' ? all.filter(r => r.status !== 'matched')
                      : all.filter(r => r.status === reconStatusFilter);
                    if (filtered.length > 0) return null;
                    const label = reconStatusFilter === 'discrepancies' ? 'discrepancy'
                      : reconStatusFilter === 'matched' ? 'matched'
                      : reconStatusFilter === 'bank_higher' ? 'Bank > App'
                      : reconStatusFilter === 'app_higher' ? 'App > Bank'
                      : reconStatusFilter === 'bank_only' ? 'Bank Only'
                      : reconStatusFilter === 'app_only' ? 'App Only'
                      : reconStatusFilter;
                    const isAllGood = reconStatusFilter === 'discrepancies';
                    return (
                      <tr><td colSpan={8} className={`p-6 text-center text-sm font-medium ${isAllGood ? 'text-success' : 'text-muted-foreground'}`} data-testid="recon-empty-filter">
                        {isAllGood ? `✓ All ${all.length} rows are matched — nothing to investigate.` : `No "${label}" rows in this period.`}
                        {' '}
                        <button type="button" className="underline text-info" onClick={() => setReconStatusFilter('all')} data-testid="recon-show-all-btn">Show all</button>
                      </td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Quick Investigate Dialog */}
      <Dialog open={!!reconInvestigate} onOpenChange={(o) => { if (!o) setReconInvestigate(null); }}>
        <DialogContent className="max-w-5xl max-h-[85dvh] overflow-y-auto" data-testid="recon-investigate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={16} /> Investigate reconciliation row
            </DialogTitle>
          </DialogHeader>
          {reconInvestigate?.loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="animate-spin mr-2" size={16} /> Loading underlying transactions…
            </div>
          ) : reconInvestigate?.data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="p-2 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Bank deposit date</div>
                  <div className="font-medium">{reconInvestigate.data.deposit_date}</div>
                </div>
                <div className="p-2 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Sale date</div>
                  <div className="font-medium">{reconInvestigate.data.sale_date}</div>
                </div>
                <div className="p-2 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Branch</div>
                  <div className="font-medium truncate" title={reconInvestigate.data.branch}>{reconInvestigate.data.branch}</div>
                </div>
                <div className={`p-2 rounded-lg ${Math.abs(reconInvestigate.data.difference) < 1 ? 'bg-success/10' : 'bg-error/10'}`}>
                  <div className="text-xs text-muted-foreground">Difference (Bank − App)</div>
                  <div className={`font-bold ${Math.abs(reconInvestigate.data.difference) < 1 ? 'text-success' : 'text-error'}`}>
                    {reconInvestigate.data.difference > 0 ? '+' : ''}{fmt(reconInvestigate.data.difference)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-success/10 flex items-center justify-between">
                    <div className="text-sm font-semibold text-success flex items-center gap-1">
                      <Landmark size={14} /> Bank deposits
                    </div>
                    <div className="text-sm font-bold text-success">{fmt(reconInvestigate.data.bank_total)}</div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="recon-investigate-bank-table">
                        <thead className="sticky top-0 bg-muted/50">
                          <tr className="border-b">
                            <th className="text-left p-2 font-medium">POS</th>
                            <th className="text-left p-2 font-medium">Description</th>
                            <th className="text-right p-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(reconInvestigate.data.bank_lines || []).map((b, i) => (
                            <tr key={i} className="border-b hover:bg-muted/30" data-testid={`recon-investigate-bank-row-${i}`}>
                              <td className="p-2">
                                <div className="font-medium">{b.machine_name}</div>
                                {b.machine_id && <div className="text-muted-foreground font-mono text-[10px]">{b.machine_id}</div>}
                              </td>
                              <td className="p-2 text-muted-foreground truncate max-w-[180px]" title={b.description}>{b.description || '—'}</td>
                              <td className="p-2 text-right font-medium text-success">{fmt(b.amount)}</td>
                            </tr>
                          ))}
                          {(reconInvestigate.data.bank_lines || []).length === 0 && (
                            <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">No bank POS deposits for this row.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="border rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-primary/10 flex items-center justify-between">
                    <div className="text-sm font-semibold text-primary flex items-center gap-1">
                      <FileText size={14} /> App sales (bank-paid portion)
                    </div>
                    <div className="text-sm font-bold text-primary">{fmt(reconInvestigate.data.app_total)}</div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="recon-investigate-app-table">
                        <thead className="sticky top-0 bg-muted/50">
                          <tr className="border-b">
                            <th className="text-left p-2 font-medium">Customer</th>
                            <th className="text-left p-2 font-medium">Branch</th>
                            <th className="text-right p-2 font-medium">Bank Amt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(reconInvestigate.data.sale_lines || []).map((s, i) => (
                            <tr key={i} className="border-b hover:bg-muted/30" data-testid={`recon-investigate-app-row-${i}`}>
                              <td className="p-2">
                                <div className="font-medium truncate max-w-[160px]" title={s.customer}>{s.customer}</div>
                                <div className="text-muted-foreground text-[10px]">{s.items_count} item{s.items_count === 1 ? '' : 's'} · Total {fmt0(s.total)}</div>
                              </td>
                              <td className="p-2 text-muted-foreground">{s.branch_name}</td>
                              <td className="p-2 text-right font-medium text-primary">{fmt(s.amount)}</td>
                            </tr>
                          ))}
                          {(reconInvestigate.data.sale_lines || []).length === 0 && (
                            <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">No app sales found for this row.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {Math.abs(reconInvestigate.data.difference) >= 1 && (
                <div className={`p-3 rounded-lg text-xs ${reconInvestigate.data.difference > 0 ? 'bg-orange-50 text-orange-800' : 'bg-rose-50 text-rose-800'}`}>
                  {reconInvestigate.data.difference > 0
                    ? <>Bank received <b>{fmt(Math.abs(reconInvestigate.data.difference))}</b> more than recorded sales. Likely an un-recorded sale or a previous day's deposit landing here.</>
                    : <>App recorded <b>{fmt(Math.abs(reconInvestigate.data.difference))}</b> more than the bank deposit. Check for partial settlements, fees deducted, or a sale that hasn't cleared yet.</>}
                </div>
              )}

              {Math.abs(reconInvestigate.data.difference) >= 1 && (
                <div className="border border-purple-200 bg-purple-50/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-purple-900">
                      <Sparkles size={14} /> AI Reconciliation Assistant
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-purple-300 hover:bg-purple-100"
                        onClick={autoExplainRecon}
                        disabled={!!reconInvestigate.explainLoading}
                        data-testid="recon-investigate-explain-btn"
                      >
                        {reconInvestigate.explainLoading
                          ? <><Loader2 size={12} className="mr-1 animate-spin" /> Analysing…</>
                          : reconInvestigate.explanation
                            ? <><Sparkles size={12} className="mr-1" /> Re-analyse</>
                            : <><Sparkles size={12} className="mr-1" /> Auto-explain discrepancy</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-purple-700 hover:bg-purple-100"
                        onClick={() => {
                          setReconInvestigate(null);
                          setTimeout(() => runBatchExplain(), 100);
                        }}
                        data-testid="recon-investigate-open-batch"
                        title="See this discrepancy in the context of the full monthly-close report"
                      >
                        See monthly report →
                      </Button>
                    </div>
                  </div>
                  {reconInvestigate.explanation && (
                    <div className="text-xs text-purple-900 leading-relaxed bg-white/70 rounded p-2 border border-purple-100 whitespace-pre-wrap" data-testid="recon-investigate-explanation">
                      {reconInvestigate.explanation}
                    </div>
                  )}
                  {!reconInvestigate.explanation && !reconInvestigate.explainLoading && (
                    <div className="text-[11px] text-purple-700/70 italic">Click to ask GPT-4.1-mini to read the bank vs app lines and suggest the most likely cause + next action.</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No data</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconInvestigate(null)} data-testid="recon-investigate-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch AI Monthly-Close Report Dialog */}
      <Dialog open={!!batchExplain} onOpenChange={(o) => { if (!o) setBatchExplain(null); }}>
        <DialogContent className="max-w-3xl max-h-[88dvh] overflow-y-auto" data-testid="recon-batch-explain-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-purple-600" /> AI Monthly-Close Report
            </DialogTitle>
          </DialogHeader>
          {batchExplain?.loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="animate-spin" size={24} />
              <div className="text-sm">Reading every discrepancy and writing your monthly-close narrative…</div>
              <div className="text-[11px]">This usually takes 8-20 seconds.</div>
            </div>
          ) : batchExplain?.narrative ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-muted/50 rounded-lg">
                  <div className="text-muted-foreground">Total discrepancies</div>
                  <div className="font-bold text-base">{batchExplain.discrepancy_count}</div>
                </div>
                <div className="p-2 bg-muted/50 rounded-lg">
                  <div className="text-muted-foreground">Explained</div>
                  <div className="font-bold text-base">{batchExplain.explained_count}{batchExplain.skipped_count > 0 ? ` (${batchExplain.skipped_count} skipped)` : ''}</div>
                </div>
                <div className="p-2 bg-error/10 rounded-lg">
                  <div className="text-error/80">Combined gap</div>
                  <div className="font-bold text-base text-error">{cur()} {Number(batchExplain.total_gap || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              </div>
              <div
                className="prose prose-sm max-w-none p-4 bg-purple-50/50 border border-purple-200 rounded-lg whitespace-pre-wrap text-sm leading-relaxed"
                data-testid="recon-batch-explain-narrative"
              >
                {batchExplain.narrative}
              </div>
              {Array.isArray(batchExplain.rows) && batchExplain.rows.length > 0 && (
                <div className="rounded-lg border border-purple-200 overflow-hidden" data-testid="recon-batch-explain-rows">
                  <div className="px-3 py-1.5 bg-purple-100/60 text-[10px] font-semibold uppercase text-purple-800">
                    Click any row to drill into it
                  </div>
                  <div className="max-h-[180px] overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="text-left p-1.5 font-medium">Deposit date</th>
                            <th className="text-left p-1.5 font-medium">Branch</th>
                            <th className="text-right p-1.5 font-medium">Difference</th>
                            <th className="text-left p-1.5 font-medium">Status</th>
                            <th className="text-right p-1.5 font-medium w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchExplain.rows.map((r, i) => (
                            <tr key={i} className="border-t hover:bg-purple-50/40 cursor-pointer" data-testid={`recon-batch-explain-row-${i}`}>
                              <td className="p-1.5">{r.deposit_date}</td>
                              <td className="p-1.5 truncate max-w-[180px]" title={r.branch}>{r.branch}</td>
                              <td className={`p-1.5 text-right font-mono font-semibold ${r.difference > 0 ? 'text-orange-700' : 'text-rose-700'}`}>{r.difference > 0 ? '+' : ''}{Number(r.difference).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="p-1.5">
                                <span className="text-[9px] uppercase text-muted-foreground">{r.status}</span>
                              </td>
                              <td className="p-1.5 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-1.5 text-[10px] text-purple-700 hover:bg-purple-100"
                                  onClick={() => {
                                    setBatchExplain(null);
                                    openReconInvestigate({
                                      deposit_date: r.deposit_date,
                                      branch: r.branch,
                                      status: r.status,
                                    });
                                  }}
                                  data-testid={`recon-batch-explain-drill-${i}`}
                                  title="Open the Investigate drill-down for this row"
                                >
                                  <Eye size={11} className="mr-0.5" /> Drill
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <div className="text-[11px] text-muted-foreground italic">
                  Generated by gpt-4.1-mini. Treat as a starting point — always verify with your bank statement and POS reports.
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={saveBatchExplainAsNote}
                    disabled={savingNote}
                    className="border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                    data-testid="recon-batch-explain-save"
                    title="Persist this report against the statement (audit trail + Notes tab)"
                  >
                    {savingNote ? (
                      <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</>
                    ) : (
                      <><Bookmark size={14} className="mr-1" /> Save as Month-End Note</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyShareUrl({ includeAi: true })}
                    className="border-sky-400 text-sky-800 hover:bg-sky-50"
                    data-testid="recon-batch-explain-share-link"
                    title="Copy a deep-link URL that auto-opens this AI report — single-link Slack drop for auditors"
                  >
                    <Link2 size={14} className="mr-1" /> Share report link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(batchExplain.narrative);
                        toast.success('Report copied to clipboard');
                      } catch {
                        window.prompt('Copy this report manually:', batchExplain.narrative);
                      }
                    }}
                    data-testid="recon-batch-explain-copy"
                  >
                    <Check size={14} className="mr-1" /> Copy report
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchExplain(null)} data-testid="recon-batch-explain-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
